import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { EnhancedTabs } from '@swyftx/aviary/molecules/EnhancedTabs';
import { LoansFeaturePromoBanner } from '@swyftx/features/loans/components';
import { useLoansFeaturePromoBanner } from '@swyftx/features/loans/hooks/useLoansFeaturePromoBanner';

import { MarginAccountSummary } from '@routes/SwyftxMargin/components/Account';
import { SwyftxMarginMaintenanceBanner } from '@routes/SwyftxMargin/components/General';

import { WalletList, WalletMetrics, WalletPortfolioAllocation, WalletPortfolioPerformance } from '@Wallet/components';

import { observer } from 'mobx-react-lite';
import { useSwyftxMargin } from 'src/lib/margin';
import { useMarginAccounts } from 'src/lib/margin/hooks/account';
import { NavigationURLs } from 'src/lib/navigation/types';
import { SwyftxPlatform } from 'src/lib/utils/utils.types';

import { WalletType } from './types';

type WalletParams = {
  type: 'all' | 'trade' | 'earn';
};

const Wallet: React.FC = observer(() => {
  const { type } = useParams<WalletParams>();
  const { swyftxMarginEnabled } = useSwyftxMargin();
  const [platformWallet, setPlatformWallet] = useState<SwyftxPlatform>('swyftx');
  const { t } = useTranslation('wallet');
  const { data, isFetching } = useMarginAccounts();
  const { showLoansPromoBanner } = useLoansFeaturePromoBanner();

  const navigate = useNavigate();

  const isValidType = (walletType: WalletParams | string | undefined): walletType is WalletParams =>
    walletType === undefined || walletType === 'all' || walletType === 'trade';

  useEffect(() => {
    if (!isValidType(type)) {
      navigate(NavigationURLs.WalletsRoot);
    }
  }, [type, navigate]);

  return (
    <>
      <FlexLayout direction='column' className='p-16 @container sm:p-24 sm:pr-0' spacing={16}>
        {!!data?.accounts.length && !isFetching && swyftxMarginEnabled && (
          <EnhancedTabs<SwyftxPlatform>
            variant='parent'
            size='lg'
            className='border-b border-color-border-main'
            value={platformWallet}
            onChange={setPlatformWallet}
            tabs={[
              {
                title: t('overview.tabs.derivatives.spot'),
                value: 'swyftx',
              },
              {
                title: t('overview.tabs.derivatives.derivatives'),
                value: 'derivatives',
              },
            ]}
          />
        )}
        {platformWallet === 'swyftx' && (
          <>
            <FlexLayout direction='column' className='hidden w-full @md:flex'>
              <WalletMetrics walletType={WalletType.ALL_WALLETS} />
            </FlexLayout>
            <FlexLayout direction='row' spacing={16}>
              <FlexLayout direction='column' className='w-full @md:w-5/12'>
                <Card className='h-full w-full'>
                  <WalletPortfolioAllocation walletType={WalletType.ALL_WALLETS} />
                </Card>
              </FlexLayout>
              <FlexLayout direction='column' className='hidden w-full @md:flex md:w-7/12'>
                <Card className='h-full w-full'>
                  <WalletPortfolioPerformance walletType={WalletType.ALL_WALLETS} />
                </Card>
              </FlexLayout>
            </FlexLayout>
            {showLoansPromoBanner && <LoansFeaturePromoBanner />}
            <FlexLayout className='min-h-[80px] w-full'>
              <WalletList />
            </FlexLayout>
          </>
        )}
        {platformWallet === 'derivatives' && (
          <FlexLayout direction='column' spacing={24} className='mt-24'>
            <SwyftxMarginMaintenanceBanner />
            <MarginAccountSummary hideTradeButton showAccountHealthInfo />
          </FlexLayout>
        )}
      </FlexLayout>
    </>
  );
});

export default Wallet;
